import { Headline } from '@oneaudi/unified-web-components';
import { useMemo } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { useDebugMode } from '~/app/utils/debug-mode';
import placeholder from '~/assets/icons/placeholder.svg';
import { PaintFallbackSvg } from '~/components/image-selection-tile/components/fallback-svgs/paint-fallback-svg';
import { SelectionContainer } from '~/components/image-selection-tile/style/tile-selection-section-style';
import { TilesSelection } from '~/components/image-selection-tile/tiles-selection';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { FeatureCategory, SelectableOption } from '~/graphql/types';

export function ContrastPaint() {
	const { i18n } = useI18n();
	const { roofColor } = useCatalog();
	const { getName } = useDebugMode();
	const { loadingFlags, changeConfiguration, isItemLoading, isItemChecked } = useConfigurator();
	const { config } = useMarketConfig();
	const { feature } = useVATConfig();

	const { items } = useMemo(() => {
		if (!roofColor) return { items: [], filters: [] };

		// Map items for the tile selection
		const mappedItems = roofColor.map((onePaint) => ({
			name: getName(onePaint) ?? ' ',
			key: onePaint.featureId,
			onSelect: () => {
				changeConfiguration(onePaint as SelectableOption<FeatureCategory>);
			},
			loading: isItemLoading(onePaint),
			readonly: !onePaint.status.changeable,
			familyName: onePaint.familyName,
			showRenderDisclaimer: onePaint.__item.family?.id === 'color-type:exclusive',
			standard: onePaint.status.standard,
			imgUrl: onePaint.imgUrl ? onePaint.imgUrl : placeholder,
			selected: isItemChecked(onePaint),
			price: config.showPrices ? { totalPrice: onePaint.totalPrice, feature } : undefined,
			disclaimerHeadline: onePaint.__item.texts?.disclaimerHeadline || undefined,
			disclaimerContent: onePaint.__item.texts?.disclaimerText || undefined,
			disclaimerStartDate: onePaint.__item.texts?.disclaimerStartDate || undefined,
			disclaimerEndDate: onePaint.__item.texts?.disclaimerEndDate || undefined,
		}));

		const selectedColor = mappedItems.find((color) => {
			return color.selected;
		});

		if (!selectedColor) return { filters: [], items: [] };

		return {
			filters: [],
			items: mappedItems,
		};
	}, [
		JSON.stringify(roofColor),
		changeConfiguration,
		config.showPrices,
		getName,
		isItemChecked,
		isItemLoading,
		feature,
	]);

	// FIXME: remove later
	// this is only short term for UK to hide contrast paint until they figured out what they want
	const { carIdentifier, carlineId } = config;
	const hideContrastPaint = carIdentifier.country === 'gb' && carlineId.includes('a1');
	if (hideContrastPaint) return null;
	// FIXME: remove later

	return (
		<>
			<SelectionContainer items={roofColor?.length} tabIndex={0}>
				<Headline className="subtitle" tag="h2" variant={{ order: '4', type: 'headline', weight: 'normal' }}>
					{i18n('ngc.equipment.contrastPaints')}
				</Headline>
				<TilesSelection
					id="contrastPaint"
					items={items}
					fallbackSVG={PaintFallbackSvg()}
					isLoading={loadingFlags.checkingConflicts}
					forcedWayOfFilter="no-filter"
					tileHorizontalCenter
				/>
			</SelectionContainer>
		</>
	);
}
